import { useEffect, useMemo, useRef, useState } from 'react';
import { Headphones, MessageCircle, Send, X } from 'lucide-react';
import { getApiBaseUrl } from '../lib/config';

/**
 * Floating "leave-a-message" support chat for the public marketing
 * site. Mounted at the app root so it appears on every public page —
 * landing, pricing, docs, legal, status, auth — without each route
 * having to opt in.
 *
 * Architecture (matches the API route at /api/support/chat):
 *
 *   1. First message: visitor enters email + message → POST creates a
 *      thread, persists the message, fires an email to support@rynora.app
 *      and an ack email to the visitor.
 *   2. Subsequent messages reuse the threadId stored in localStorage
 *      so the conversation survives refreshes and tab restarts.
 *   3. Support team replies via email — those replies land directly in
 *      the visitor's inbox because the outbound mail's replyTo is the
 *      visitor's address. We don't try to render the agent's reply
 *      back into the widget; that would require either polling or a
 *      websocket and we deliberately stay zero-infra here.
 *
 * The widget is fully keyboard-accessible (Esc closes, focus traps to
 * the panel when open, aria-live="polite" on the transcript) and
 * respects `prefers-reduced-motion` for the open/close animations.
 */
type Message = {
  id: string;
  sender: 'user' | 'system' | 'agent';
  body: string;
  createdAt: string;
};

type Status = 'idle' | 'sending' | 'error';

interface PostResponse {
  threadId: string;
  messageId: string;
  createdAt: string;
}

const STORAGE_THREAD_KEY = 'rynora-support-thread';
const STORAGE_EMAIL_KEY = 'rynora-support-email';
const STORAGE_NAME_KEY = 'rynora-support-name';
const STORAGE_HISTORY_KEY = 'rynora-support-messages';

const SYSTEM_GREETING: Message = {
  id: 'system-greeting',
  sender: 'system',
  body: "Hi! I'm the Rynora support inbox. Send a note here and a human will reply directly to your email — usually the same day, never later than 48 hours.",
  createdAt: new Date(0).toISOString(),
};

function getApiBase(): string {
  return getApiBaseUrl();
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function readLocal<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable; non-fatal */
  }
}

function readString(key: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function writeString(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    /* non-fatal */
  }
}

function formatTimestamp(iso: string): string {
  if (!iso || iso.startsWith('1970')) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function SupportChat() {
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState<string>(() => readString(STORAGE_THREAD_KEY));
  const [email, setEmail] = useState<string>(() => readString(STORAGE_EMAIL_KEY));
  const [name, setName] = useState<string>(() => readString(STORAGE_NAME_KEY));
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<Message[]>(() => {
    const cached = readLocal<Message[]>(STORAGE_HISTORY_KEY);
    return cached && cached.length > 0 ? cached : [SYSTEM_GREETING];
  });
  const [status, setStatus] = useState<Status>('idle');
  const [errorText, setErrorText] = useState<string | null>(null);

  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  // Persist message history so refreshing the page keeps the
  // conversation visible until the user explicitly closes the thread.
  useEffect(() => {
    writeLocal(STORAGE_HISTORY_KEY, messages);
  }, [messages]);

  // Auto-scroll the transcript whenever a new message lands or the
  // panel is reopened so the user always sees the latest line.
  useEffect(() => {
    if (!open || !transcriptRef.current) return;
    transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [open, messages.length]);

  // Esc closes the panel — table-stakes for any modal-ish UI.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // When opening, focus the right input — email if we don't have it
  // yet, message field otherwise.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      if (!email) emailInputRef.current?.focus();
      else textareaRef.current?.focus();
    }, 80);
    return () => window.clearTimeout(t);
  }, [open, email]);

  const needsEmail = !email || !threadId;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorText(null);
    const trimmedDraft = draft.trim();
    if (!trimmedDraft) return;
    if (trimmedDraft.length < 2) {
      setErrorText('Message is a bit short — give us at least a couple of words.');
      return;
    }

    if (needsEmail) {
      const trimmedEmail = email.trim().toLowerCase();
      if (!isValidEmail(trimmedEmail)) {
        setErrorText('Please enter a valid email so we can reply.');
        emailInputRef.current?.focus();
        return;
      }
    }

    setStatus('sending');

    const apiBase = getApiBase();
    const payload = threadId
      ? { threadId, message: trimmedDraft }
      : {
          email: email.trim().toLowerCase(),
          name: name.trim() || undefined,
          message: trimmedDraft,
          source: typeof window !== 'undefined' ? `rynora.app${window.location.pathname}` : 'rynora.app',
        };

    try {
      const res = await fetch(`${apiBase}/api/support/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = (await res.json().catch(() => ({}))) as PostResponse & { error?: string };
      if (!res.ok) {
        setStatus('error');
        setErrorText(json.error || "Couldn't send your message — please try again in a moment.");
        return;
      }

      const isFirst = !threadId;
      if (isFirst) {
        setThreadId(json.threadId);
        writeString(STORAGE_THREAD_KEY, json.threadId);
        writeString(STORAGE_EMAIL_KEY, email.trim().toLowerCase());
        if (name.trim()) writeString(STORAGE_NAME_KEY, name.trim());
      }

      const userMsg: Message = {
        id: json.messageId,
        sender: 'user',
        body: trimmedDraft,
        createdAt: json.createdAt || new Date().toISOString(),
      };
      const ack: Message = {
        id: `ack-${json.messageId}`,
        sender: 'system',
        body: isFirst
          ? `Got it — sent to support@rynora.app. We'll reply to ${email.trim().toLowerCase()} within 48 hours.`
          : "Sent. We'll get back to you on email.",
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMsg, ack]);
      setDraft('');
      setStatus('idle');
    } catch {
      setStatus('error');
      setErrorText('Network error — please check your connection and try again.');
    }
  };

  const onReset = () => {
    if (typeof window !== 'undefined') {
      const ok = window.confirm(
        'Close this conversation? You can start a new one anytime, but the messages above will be cleared from this browser.',
      );
      if (!ok) return;
    }
    writeString(STORAGE_THREAD_KEY, '');
    writeString(STORAGE_HISTORY_KEY, '' as unknown as string);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_HISTORY_KEY);
    }
    setThreadId('');
    setMessages([SYSTEM_GREETING]);
    setDraft('');
    setErrorText(null);
    setStatus('idle');
  };

  const unread = useMemo(() => 0, []); // placeholder for future agent-reply notifications

  return (
    <>
      {!open && (
        <button
          className="rynora-chat__bubble"
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open support chat"
        >
          <MessageCircle size={22} aria-hidden="true" />
          <span className="rynora-chat__bubble-text">Chat with support</span>
          {unread > 0 && (
            <span className="rynora-chat__badge" aria-label={`${unread} unread`}>
              {unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div className="rynora-chat__panel" role="dialog" aria-label="Rynora support chat">
          <header className="rynora-chat__header">
            <div className="rynora-chat__header-id">
              <span className="rynora-chat__avatar" aria-hidden="true">
                <Headphones size={16} />
              </span>
              <div>
                <strong>Rynora support</strong>
                <small>Replies via email · usually within 48h</small>
              </div>
            </div>
            <button
              type="button"
              className="rynora-chat__icon-btn"
              onClick={() => setOpen(false)}
              aria-label="Close support chat"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </header>

          <div
            className="rynora-chat__transcript"
            ref={transcriptRef}
            role="log"
            aria-live="polite"
            aria-relevant="additions"
          >
            {messages.map((m) => (
              <div key={m.id} className={`rynora-chat__msg rynora-chat__msg--${m.sender}`}>
                {m.sender !== 'user' && (
                  <span className="rynora-chat__msg-avatar" aria-hidden="true">
                    <Headphones size={12} />
                  </span>
                )}
                <div className="rynora-chat__msg-bubble">
                  <p>{m.body}</p>
                  {formatTimestamp(m.createdAt) && (
                    <time className="rynora-chat__msg-time">{formatTimestamp(m.createdAt)}</time>
                  )}
                </div>
              </div>
            ))}
            {status === 'sending' && (
              <div className="rynora-chat__msg rynora-chat__msg--system">
                <span className="rynora-chat__msg-avatar" aria-hidden="true">
                  <Headphones size={12} />
                </span>
                <div className="rynora-chat__msg-bubble rynora-chat__msg-bubble--typing">
                  <span /> <span /> <span />
                </div>
              </div>
            )}
          </div>

          <form className="rynora-chat__form" onSubmit={onSubmit}>
            {needsEmail && (
              <div className="rynora-chat__lead">
                <label className="rynora-chat__field">
                  <span>Your email</span>
                  <input
                    ref={emailInputRef}
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>
                <label className="rynora-chat__field">
                  <span>
                    Name <em>(optional)</em>
                  </span>
                  <input
                    type="text"
                    autoComplete="name"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>
              </div>
            )}

            {errorText && (
              <div className="rynora-chat__error" role="alert">
                {errorText}
              </div>
            )}

            <div className="rynora-chat__compose">
              <textarea
                ref={textareaRef}
                className="rynora-chat__textarea"
                placeholder="Type your message…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  // Cmd/Ctrl+Enter sends. Plain Enter still inserts a
                  // newline so multi-paragraph notes are easy to write.
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    onSubmit(e as unknown as React.FormEvent);
                  }
                }}
                rows={2}
                maxLength={4000}
                disabled={status === 'sending'}
              />
              <button
                type="submit"
                className="rynora-chat__send"
                disabled={status === 'sending' || !draft.trim()}
                aria-label="Send message"
              >
                <Send size={16} aria-hidden="true" />
              </button>
            </div>

            <div className="rynora-chat__footer">
              <span>
                {threadId ? (
                  <button type="button" className="rynora-chat__link" onClick={onReset}>
                    End conversation
                  </button>
                ) : (
                  <>
                    Sent to <strong>support@rynora.app</strong>
                  </>
                )}
              </span>
              <span className="rynora-chat__counter">{draft.length}/4000</span>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
